import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import {
  WizValidatorFn,
  WizValidators,
  WizValidationError,
  WizValidationErrors,
} from './../../../services/validators/wiz-validators';
import { BASE_COMPONENT, WizBaseComponent } from '../../core/wiz-base-component';
import { WizFocusManager } from '../../../services/core/focus/wiz-focus-manager';
import { WizCheckbox } from '../button/wiz-checkbox/wiz-checkbox';

import { WizAutocompleteTrigger } from './wiz-autocomplete-directive';

import { MatIcon } from '@angular/material/icon';
import { WizLabel } from '../wiz-label/wiz-label';

import { MatLegacyOption as MatOption } from '@angular/material/legacy-core';
import { WizRoot } from '../../views/wiz-root';
import { WizBaseCollector } from '../../core/wiz-base-collector';
import { IWIZ_COMPONENT} from '../../../interfaces/iwiz-component';
import {
  CODE_SPACE,
  VALUE_BACK_SPACE,
  VALUE_DELETE,
  VALUE_DOWN,
  VALUE_ENTER,
  VALUE_ESCAPE,
  VALUE_L,
  VALUE_TAB,
  VALUE_UP,
  VALUE_X, FOCUS_MODE, WDSChanges, WizStringUtils, WIZ_EVENT_TYPE, WIZ_BOOLEAN, EXO_VALUE, WIZ_ERROR_MESSAGE } from '@wizeoo/akm-core-lib';
import { WizTranslableProperty } from '../../../services/translation/wiz-translatable-property.decorator';
import { WizEventManager } from '../../../events/wiz-event.manager';
import { WizEvent } from '../../../events/wiz-event';

@Component({
  selector: 'wiz-dropdown',
  templateUrl: './wiz-dropdown.html',
  styleUrls: ['./wiz-dropdown.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizDropdown) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizDropdown) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizDropdown) },
  ],
})
export class WizDropdown extends WizBaseCollector implements AfterViewInit, OnDestroy {
  /** Constructor with translation engine
   * @param cdref: ChangeDetectorRef
   * @param elementRef: the dom of the component
   * @param renderer: object that will allow the interaction between the component and the DOM
   * @param ngZone: service injecter pour des exécutions à l'intérieur ou à l'extérieur de la zone Angular.
   */
  constructor(cdref: ChangeDetectorRef, elementRef: ElementRef, renderer: Renderer2, public ngZone: NgZone) {
    super(cdref, elementRef, renderer, ngZone);
    this.setWizFocusMode(FOCUS_MODE.ROLLBACK); // pas de sortie en erreur sur une combo
    this.checkStyle();

    // neutralisation du statut "collector". La drop va se comporter comme les compos de base pour
    // le dispatch des ModelUpdate. L'aspect "Collector" est neutralisé par l'override de onProviderChanges
    this.basicComponent = true;
  }

  // l'input
  @ViewChild('innerInput', { read: ElementRef }) private innerInput: ElementRef;
  // l'input vu comme un déclencheur de popUp pour l'autoComplette
  @ViewChild('innerAsTrigger', { read: WizAutocompleteTrigger })
  private innerAsTrigger: WizAutocompleteTrigger;

  // flèche bas
  @ViewChild('innerDownArrow', { read: MatIcon })
  private innerDownArrow: MatIcon;
  // croix pour clear du compo
  @ViewChild('innerClearIcon', { read: MatIcon })
  protected innerClearIcon: MatIcon;
  // label pour affichage du hint
  @ViewChild('innerhint', { read: WizLabel, static: true })
  protected innerhint: WizLabel;
  // container en bas pour injection message d'erreur
  @ViewChild('errorContainer', { read: ViewContainerRef, static: true })
  protected errorContainer: ViewContainerRef;
  // l'autoComplete liée
  @ViewChild('dropPanel') private dropPanel: MatAutocomplete;

  // la checkbox de selection ALL / None
  @ViewChild('selectAllCheck', { read: WizCheckbox })
  selectAllCheck: WizCheckbox;
  // le renderer de base (texte simple en span)
  @ViewChild('defaultRenderer', { read: TemplateRef, static: true })
  declare defaultRenderer: TemplateRef<any>;
  // le renderer dynamique pour affichage du WizRenderer
  @ViewChild('dynamicRenderer', { read: TemplateRef, static: true })
  declare dynamicRenderer: TemplateRef<any>;

  /** skinning */
  public buttonModeClassName: string = 'buttonmode-class'; // class css pour mode bouton
  public disabledClassName: string = 'disabled-class'; // class css pour skin "disabled"

  // ####
  // ####
  // #### configuration ####
  // ####
  // ####

  private _wizShowErrors: boolean = false; // afficher les erreurs sous le bouton
  // attention, le composant doit porter la directive WizValidator pour être pris en compte
  @Input()
  public set wizShowErrors(v: boolean) {
    this.setWizShowErrors(v);
  }
  public get wizShowErrors(): boolean {
    return this.getWizShowErrors();
  }

  /**
   * methodes surchargeables condition d'affichage ou non du message d'erreur
   */
  protected setWizShowErrors(v: boolean) {
    this._wizShowErrors = v;
    this.cdref.markForCheck();
  }
  protected getWizShowErrors(): boolean {
    return this._wizShowErrors;
  }
  // prompt traduit, injecté depuis wizPlaceholder
  // translatedPlaceholder est initialisé par une chaine vide pour éviter d'afficher undefined
  public translatedPlaceholder: string = '';
  private _wizPlaceholder: string;
  @WizTranslableProperty('translatedPlaceholder') // décorateur de méthode pour traduction du prompt
  @Input()
  public set wizPlaceholder(str: string) {
    this.setWizPlaceholder(str);
  }
  public get wizPlaceholder(): string {
    return this.getWizPlaceholder();
  }
  /**
   * methodes surchargeables du wizPlaceholder
   */
  protected setWizPlaceholder(str: string) {
    this._wizPlaceholder = str;
  }
  protected getWizPlaceholder(): string {
    return this._wizPlaceholder;
  }

  private _wizHint: string; // le hint, sera traduit par son wizLabel
  @Input() public set wizHint(v: string) {
    this.setWizHint(v);
  }
  public get wizHint(): string {
    return this.getWizHint();
  }
  /**
   * methodes surchargeables du message complémentaire du compo
   */
  protected setWizHint(v: string) {
    this._wizHint = v;
    this.cdref.markForCheck();
  }
  protected getWizHint(): string {
    return this._wizHint;
  }

  /** largeur de la popUp */
  @Input() public set wizPanelWidth(width: string) {
    this.setWizPanelWidth(width);
  }
  public get wizPanelWidth(): string {
    return this.getWizPanelWidth();
  }
  private _wizPanelWidth = '200px';
  /**
   * methodes surchargeables de la taille du panel de la drop
   */
  protected setWizPanelWidth(width: string) {
    this._wizPanelWidth = width;
    this.cdref.markForCheck();
  }
  protected getWizPanelWidth(): string {
    return this._wizPanelWidth;
  }

  /** affichage du filtre (saisie = true) */
  @Input() public set wizShowSearchInput(wizShowSearchInput: boolean) {
    this.setWizShowSearchInput(wizShowSearchInput);
  }
  public get wizShowSearchInput(): boolean {
    return this.getWizShowSearchInput();
  }
  private _wizShowSearchInput: boolean = false;
  /**
   * methodes surchargeables de l'affichage du filtre
   */
  protected setWizShowSearchInput(wizShowSearchInput: boolean) {
    // pas de mode manuel en multi selection
    this._wizShowSearchInput = this.isMultiSelection() ? false : wizShowSearchInput;
    this.cdref.markForCheck();
  }
  protected getWizShowSearchInput(): boolean {
    return this._wizShowSearchInput;
  }

  /** label du fakeItem */
  private _wizFakeLabel: string = '';
  @Input() public set wizFakeLabel(str: string) {
    this.setWizFakeLabel(str);
  }

  public get wizFakeLabel(): string {
    return this.getWizFakeLabel();
  }

  /**
   * methodes surchargeables du fakeLabel
   */
  protected setWizFakeLabel(str: string) {
    this._wizFakeLabel = str;
  }

  protected getWizFakeLabel(): string {
    return this._wizFakeLabel;
  }

  /** champ utilisé pour l'affichage dans l'input on override pour mettre à jour l'innerInputString */
  @Input() public set wizLabelField(wizLabelField: string) {
    this._wizLabelField = wizLabelField;
    this.innerInputString = this.getInnerLabel();
  }

  public get wizLabelField(): string {
    return this._wizLabelField;
  }

  // ####
  // ####
  // #### gestion de la valeur et des erreurs ####
  // ####
  // ####/

  /** le dernier string correct pour alimentation de l'input */
  public innerInputString: string = '';

  protected _componentIsActive: boolean = false; // le focus est pris (car initialValue peut être null)
  protected _wizInitialValue: any[] = null; // initiale , alimentée sur prise de focus

  private _hasErrors: boolean = false; // le composant a à minima une erreur
  public get hasErrors(): boolean {
    return this.getHasErrors();
  }
  /**
   * methode surchargeable accesseur du hasErrors
   */
  protected getHasErrors(): boolean {
    return this._hasErrors;
  }

  /** accesseur sur nb items sélectionnés (réel, pas le jeux de travail de la drop) */
  public nbSelected(): number {
    return this.wizDataProvider && this.wizDataProvider.selectedItems ? this.wizDataProvider.selectedItems.length : 0;
  }

  /** une map d'objets, la clé est l'instance */
  public setWizValue(value: any[]) {
    if (!this._lastGoodValue) {
      // alimentation initiale de la lastGood value
      this._lastGoodValue = value;
    }
    // neutralisation des undefined
    if (value === undefined) {
      // console.log('setWizValue', 'neutralize undefined');
      value = [];
    }
    super.setWizValue(value);

    this.updatePopSelectedItems();

    // on affecte également la sélection sur le wizDataProvider
    // this.wizDataProvider.select(value);

    // alimentation du libellé de l'input

    // check _componentIsActive pour ne pas afficher d'erreur quand la valeur est setter par programmation
    if (!this._componentIsActive) {
      this._hasErrors = false;
    } else if (this.wizMandatory && (!value || value.length === 0)) {
      // pas de valeur et mandatory
      this._hasErrors = true;
    } else if (this._wizInitialValue === null) {
      // check wizInitialValue pour ne pas afficher d'erreur quand la valeur est setter par programmation
      // console.log('_wizInitialValue null');
      this._hasErrors = false;
    } else {
      this._hasErrors = false;
    }

    this.manageErrorDisplay(); // reset de l'erreur si existe
    this.cdref.markForCheck();
  }

  /** alimentation par collection d'objet */
  public get selectedItems(): any[] {
    return this.getSelectedItems();
  }

  /**
   * methodes surchargeables,
   * alimentation de la wizValue par collection d'objet
   * @param values, items selectionnés
   */
  protected getSelectedItems(): any[] {
    return this.wizDataProvider.selectedItems;
  }

  /** alimentation par objet unique */
  /** Selected item value in mono selection mode */
  public get selectedItem(): any {
    return this.getSelectedItem();
  }

  /**
   * methodes surchargeables,
   * alimentation par objet unique
   * @param value, la nouvelle valeur du selectedItems
   */
  protected getSelectedItem(): any {
    return this.wizDataProvider.selectedItem;
  }

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####
  /** flag signalant que la popUp est ouverte (pour maintient du skin "focus") */
  public popUpOpened: boolean = false;

  protected onProviderChanges(changes: WDSChanges) {
    if (changes) {
      if (changes.type === 'init' || changes.type === 'refreshed') {
        this.wizDataProvider.addFilter(this.innerFilterFunc.bind(this));
      }

      if (changes.type === 'init' || changes.type === 'selectionChanged' || changes.type === 'programmaticChanged') {
        // on set la value dans un premier temps car le modelUpdate est dispatché par le super.onProviderChanges
        this.setWizValue(this.wizDataProvider.selectedItems);
        super.onProviderChanges(changes);
        this.innerInputString = this.getInnerLabel();
        this.updatePopSelectedItems();
      }
      this.cdref.markForCheck();
    }
  }

  /** retourne la chaine de recherche en cours (ou null) */
  public searchString(): string {
    if (!this.wizShowSearchInput) {
      // rien si pas de mode saisie
      return null;
    }
    if (!this.innerInput) {
      // pas instancié
      return null;
    }
    return this.innerInput.nativeElement.value;
  }

  /** suppression de la clé de filtre (programmatic) */
  public clearSearchString(): string {
    if (!this.wizShowSearchInput) {
      // rien si pas de mode saisie
      return null;
    }
    if (!this.innerInput) {
      // pas instancié
      return null;
    }
    this.innerInput.nativeElement.value = null;
  }

  /** FilterFunc basée sur le search */
  private innerFilterFunc(item: any): boolean {
    if (!this.searchString()) {
      return true;
    }

    if (!item && !this.getItemLabel(item)) {
      // fake item
      return false;
    }

    if (WizStringUtils.containUpper(this.getItemLabel(item), this.searchString())) {
      return true;
    }
    return false;
  }

  /** réplique de la collection de selected pour alimentation de la popUp (pour revert) */
  public popUpSelectedItems: Map<any, any> = new Map<any, any>();

  /** détermine si l'élément est sélectionné DANS LA POPUP */
  public isPopUpSelected(item: any): boolean {
    return this.popUpSelectedItems ? this.popUpSelectedItems.has(item) : false;
  }

  /** donne le focus à l'input depuis FocusManager */
  public wizFocus(): void {
    this.innerInput.nativeElement.select(); // select pour selectionner le contenu de l'input lors du focus
  }

  /**
   * interception de la prise de focus
   * ouverture auto de la pop si aucune sélection: NON. Doublon avec innerInputClick
   * et collision avec le close sur re-clic...
   */
  public onFocus(e: FocusEvent) {
    // Faux focus. Retour de l'un de mes éléments constitutifs
    if (WizFocusManager.getInstance().isCurrentFocused(e.target)) {
      // console.log('onFocus false',WizFocusManager.getInstance().isCurrentFocused(e.target));
      e.stopImmediatePropagation();
      e.preventDefault();
      return;
    }

    // véritable prise de focus
    if (this.getWizValue() === undefined) {
      // pas de gestion du undefined
      this._wizValue = new Map<any, any>(); // pas par le setter pour ne pas déclencher l'erreur
    }
    if (!this._componentIsActive) {
      // annulée sur focusOut véritable
      this._componentIsActive = true;
      this._wizInitialValue = this.getWizValue(); // alimentation de la valeur d'origine
    }

    super.wizGetFocus(); // super pour enregistrement auprès du focusManager
  }

  /** interception du clic direct sur l'input :
   * => demande d'ouverture du panel
   * => ou prise de focus dans la zone de saisie
   */
  public innerInputClick(e: MouseEvent): void {
    // console.log('innerInputClick',e.target);
    if (this.wizDisabled) {
      // disabled, rien à faire
      return;
    }

    if (!this.wizShowSearchInput) {
      // pas en mode saisie, ouverture du panel
      this.askForOpenPanel();
      return; // et fin
    }

    // forcément en mode saisie à partir d'ici
    if (this.innerDownArrow && e.target === this.innerDownArrow._elementRef.nativeElement) {
      // clic sur downArrow
      this.askForOpenPanel();
      return; // et fin
    }
    // mode saisie ET clic dans la zone de saisie
  }

  /** demande de fermeture du panel
   * @param rollback: boolean (false), avec annulation (sur Escape)
   */
  private askFromClosePanel(rollback: boolean = false): void {
    this.innerAsTrigger.closePanel();
  }

  /**
   * demande d'ouverture du matAutocomplete (depuis innerInput ou clic
   * sur downArrow ou downArrow/ space)
   */
  public askForOpenPanel(): void {
    if (this.wizDisabled) {
      return;
    }

    // console.log('askForPanel');
    if (this.popUpOpened) {
      // le panel est déjà ouvert, on va demander sa fermeture
      // console.log('askForPanel on openPanel, close');
      this.askFromClosePanel();
      return;
    }

    // synchronisation des éléments actifs dans la pile du dropDown
    this.updatePopSelectedItems();
    this.innerAsTrigger.openPanel();
    // synchronisation de la checkbox selectAll
    this.manageSelectAll();
  }

  /** détection de l'ouverture du panel (ouvert par le matAutocomplete)  */
  public openPanel(): void {
    // console.log('openPanel');
    // enregistrement du panel comme un enfant de focus
    setTimeout(() => {
      // console.log('show Panel',this.dropPanel.panel);
      // enregistrement de la popUp comme un enfant
      WizFocusManager.getInstance().wizRegisterOwner(this, this.dropPanel.panel);

      // alimentation du flag d'ouverture
      this.popUpOpened = true;

      // le mat-autocomplete garde les options "selected". cela pose problème avec la mécanique de preventDefault
      // sur la selection changing.
      // A l'ouverture on parcour les options pour déselectionner les options faussement sélectionnées
      if (this.dropPanel.options) {
        this.dropPanel.options.forEach((option) => {
          if (option.selected && !this.isMatOptionSelected(option)) {
            // obligé de le faire en mode "dirty" pour ne pas déclencher la chaine de sélection via la méthode "deSelect()"
            option[`_selected`] = false;
          }
        });
      }

      this.selectOption();

      // scroll vers le premier élement sélectionné
      this.dropPanel._setScrollTop(this.currentIdx * this.itemHeight);
      // pose des écouteurs sur le panel
      this.addPanelListeners();
    }, 0);
  }

  /**
   * Vérifie que l'option est bel et bien sélectionné et pas un faux positif issue du selectionChanging
   * @param pOption L'option à vérifier
   */
  private isMatOptionSelected(pOption: MatOption): boolean {
    if (this.popUpSelectedItems) {
      return this.popUpSelectedItems.has(pOption.value);
    }

    return false;
  }

  /**
   * ajout des écouteurs sur le panel, s'ils n'existent pas déjà
   */
  public addPanelListeners(): void {
    // on vérifie que le keydown n'est pas écouté
    if (!WizEventManager.hasEventListener(this, this.dropPanel.panel, 'keydown')) {
      // Ajout de l'écouteur sur le keydown DANS le panel pour maintenir la navigation clavier
      WizEventManager.addListener(this, this.dropPanel.panel, 'keydown', this._handleKeydownFromPanel);
    }
    // on vérifie que le wheel n'est pas écouté
    if (!WizEventManager.hasEventListener(this, WizRoot.getInstance().elementRef, 'wheel')) {
      // on ecoute le wheel à l'exterieur pour empécher la page de scroller
      //    et ainsi avoir le même fonctionnement que le wizDatefield et le wizTimepicker.
      WizEventManager.addListener(this, WizRoot.getInstance().elementRef, 'wheel', this.onWheelWhenOpened);
    }
  }

  /**
   * Callback de demande de changement de sélection.
   */
  protected onSelectionChanging(pEvent: WizEvent) {
    if(this.wizDataProvider) {
      // Si l'événement n'a pas été defaultPrevented, on met à jour la sélection
      if (!pEvent.defaultPrevented) {
        this.wizDataProvider.commitSelection(pEvent.value);

        // controle de delta, uniquement en mode "pas de saisie"
        if (!this.wizShowSearchInput && this.hasDelta()) {
          // dispatch
          this._wizInitialValue = this.getWizValue();
          // JRO: pas besoin géré dans le base collector
          // WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizDataProvider.selectedItems));
        }

        this.innerInput.nativeElement.value = this.innerInputString; // sinon défaut refresh
      }
    }

  }

  /**
   * interception du wheel lorsque la drop est ouverte
   * @param event, événement déclenché par un wheel
   */
  public onWheelWhenOpened(event: WheelEvent): void {
    const element: HTMLElement = event.target as HTMLElement;
    // lorsque le panel n'est pas visible, on se désabonne, pour ne empêcher l'action du wheel.
    // L'abonnement sera remis dès qu'il sera visible
    if (!this.dropPanel.showPanel) {
      // désabonnement à l'événement wheel
      WizEventManager.removeListener(this, WizRoot.getInstance().elementRef, 'wheel');
    } // le panel est visible et le wheel est effectué en dehors
    else if (!(this.dropPanel.panel.nativeElement as HTMLElement).contains(element)) {
      event.preventDefault(); // ainsi le wheel n'a aucun n'effet
      event.stopPropagation();
    }
  }

  /**
   * mise à jour de la collection des items selectionnés de la pop
   */
  public updatePopSelectedItems(): void {
    this.popUpSelectedItems = this.getWizValue()
      ? (this.getWizValue() as any[]).reduce((map: Map<any, any>, value: any) => {
          map.set(value, value);
          return map;
        }, new Map<any, any>())
      : new Map<any, any>();
  }

  /**
   * perte de focus de l'input
   */
  public onBlur(e: FocusEvent): void {
    // fausse sortie, je reviens de la popUp
    if (WizFocusManager.getInstance().isCurrentFocused(e.target)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      // console.log('wizOnBlur prevent',e, WizFocusManager.getInstance().isCurrentFocused(e.target));
      return;
    }

    // sortie véritable
    this.popUpOpened = false; // supprimer l'indicateur de focus (multi)
    this.preventClosure = false; // neutraliser le lock de fermeture
    // si le panel existe toujours à ce niveau
    if (this.dropPanel.panel) {
      // on se désabonne des listeners posés sur le panel s'ils existent, avant de le fermer, pour ne perdre son elementRef
      this.removePanelListeners();
    }
    this.askFromClosePanel(); // fermer le panel

    // dispatch du change si effectif, en mode saisie (sinon déjà fait sur fermeture de pop)
    if (this.wizShowSearchInput && this.hasDelta()) {
      // dispatch
      // console.log('exit update delta', this.getWizValue(),this._wizInitialValue);
      WizEventManager.dispatchEvent(
        this.elementRef,
        new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizDataProvider.selectedItems)
      );
    }
    // lorsque le texte saisi ne correspond pas à un element de la drop et qu'on est en mode ROLLBACk,
    // on force le refresh de l'input pour avoir la dernière valeur sélectionnée
    if (this.wizFocusMode === FOCUS_MODE.ROLLBACK && this.innerInputString !== this.innerInput.nativeElement.value) {
      this.innerInput.nativeElement.value = this.innerInputString;
      // pour indiquer que le composant a changé
      // cela permet d'afficher, l'icone d'ouverture de la drop si l'input est vide, le clearButton sinon
      this.cdref.markForCheck();
    }

    // TODO (résolu, rollback géré ailleur) eventuel rollback/ valid sur fermeture de la pop. A gérer
    this.wizLoseFocus();
    this._componentIsActive = false;
    this._wizInitialValue = null; // dans tous les cas, neutraliser initialValue
  }

  /**
   * methode de désabonnement des évenements attachés au panel de la drop
   */
  public removePanelListeners(): void {
    // si l'évenement keydown est écouté
    if (WizEventManager.hasEventListener(this, this.dropPanel.panel, 'keydown')) {
      // désabonnement à l'événement keydown
      WizEventManager.removeListener(this, this.dropPanel.panel, 'keydown');
    }
    // si l'évenement wheel est écouté
    if (WizEventManager.hasEventListener(this, WizRoot.getInstance().elementRef, 'wheel')) {
      // désabonnement à l'événement wheel
      WizEventManager.removeListener(this, WizRoot.getInstance().elementRef, 'wheel');
    }
  }

  /**
   * override
   * sur wizValidation, alimentation du wizValue avec le prototype en saisie
   */
  public prepareForCheck(): void {
    // permet de mettre à jour la wizValue avant de faire la validation du compo
    // this.setWizValue(!!this.popUpSelectedItems ? Array.from(this.popUpSelectedItems.values()) : null);
  }

  /**
   * recherche d'un delta entre la valeur initiale et "maintenant" (close / exit)
   */
  private hasDelta(): boolean {
    // comparaison des maps. En cas d'égalité, pas d'update
    let delta: boolean = false;
    const current: any[] = Array.from(this.popUpSelectedItems.keys());

    if (!current && !this._wizInitialValue) {
      // ni init ni valeur
      delta = false;
    } else if (!current || !this._wizInitialValue) {
      // plus de sélection / pas de value
      delta = true;
    } else if (current.length !== this._wizInitialValue.length) {
      // tailles différentes, forcément update
      delta = true;
    } else if (this.wizShowSearchInput && !this.isPopUpSelected(this.wizValue[0])) {
      // en mode search input, la wizValue peut être différente de l'item selectionné car on fait un clear avant d'ouvrir la pop
      delta = true;
    } else {
      // même taille, comparaison
      delta = !current.every((curr) => this._wizInitialValue.includes(curr));
    }
    return delta;
  }

  /** fermeture du panneau (avec validation) */
  public closePanel(): void {
    if (this.hasDelta()) {
      WizEventManager.dispatchEvent(
        this.innerInput,
        new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGING, this, Array.from(this.popUpSelectedItems.values()))
      );
    }

    // flag de fermeture de pop
    this.popUpOpened = false;
    this.innerInput.nativeElement.focus();

    // désabonnement aux listeners du panel
    this.removePanelListeners();
  }

  /** affichage (ou pas) du fakeItem */
  public showFakeItem(): boolean {
    if (this.isMultiSelection()) {
      // pas visible en multi
      return false;
    }

    if (this.wizMandatory) {
      // pas visible si mandatory
      return false;
    }

    // pas visible si saisie en cours (mode saisie)
    if (this.wizShowSearchInput && this.searchString()) {
      return false;
    }

    return true;
  }

  /** Determine if the select-All mat-option should be displayed */
  public showSelectAll(): boolean {
    return this.wizDataProvider && this.isMultiSelection();
  }

  /** gestion de l'état de la checkBox selectAll (3 états) */
  private manageSelectAll(): void {
    if (this.selectAllCheck) {
      // seulement en mode multi
      if (this.allSelected()) {
        this.selectAllCheck.wizChecked = true;
      } else if (this.popUpSelectedItems.size > 0) {
        // console.log('manageSelectAll', 'unknown');
        this.selectAllCheck.wizCheckState = WIZ_BOOLEAN.UNKNOWN;
      } else {
        this.selectAllCheck.wizChecked = false;
      }
    }
  }

  /**
   * Select all checkbox handler (2 états possibles, selectAll / unselectAll)
   * l'état indéfini n'est généré que par programmation
   */
  public toggleSelectAll(e: MouseEvent) {
    // accès sur click ET sur space (dans ce cas, pas de e)
    if (e) {
      e.stopPropagation(); // stop propagation, pas besoin d'arriver dans selectOption
    }
    // console.log('toggleSelectAll',this.selectAllCheck.wizChecked);
    if (this.selectAllCheck.wizChecked === true) {
      // cochée
      this.doSelectAll();
    } else {
      this.doSelectOne(null);
    }
  }

  /** Determines if all mat-options are selected (portée popUP uniquement) */
  private allSelected() {
    if (this.wizDataProvider && this.dropdownNbSelected() === this.wizDataProvider.length) {
      return true;
    }
    return false;
  }

  /**
   * détermine si l'élément est sélectionné (portée popUP uniquement)
   */
  public isSelected(item: any): boolean {
    return this.popUpSelectedItems && this.popUpSelectedItems.has(item);
  }

  /** accesseur sur nb items sélectionnés (portée popUP uniquement) */
  private dropdownNbSelected(): number {
    return this.popUpSelectedItems ? this.popUpSelectedItems.size : 0;
  }

  /** sélection de tous les éléments de la dropDown */
  private doSelectAll(): void {
    // synchronisation des éléments actifs dans la pile du dropDown
    for (const item of this.wizDataProvider) {
      this.popUpSelectedItems.set(item, item);
    }

    this.cdref.markForCheck();
  }

  /** sélection d'un élément. null = unselectAll */
  private doSelectOne(item: any): void {
    if (item) {
      if (this.isMultiSelection()) {
        if (this.popUpSelectedItems.has(item)) {
          this.popUpSelectedItems.delete(item);
        } else {
          this.popUpSelectedItems.set(item, item);
        }
      } else {
        this.popUpSelectedItems.clear();
        this.popUpSelectedItems.set(item, item);
      }
    } else {
      this.popUpSelectedItems.clear();
    }

    this.cdref.markForCheck();
  }

  /** clic sur un item de la dropDown
   * @param $event l'événement clic pour neutralisation éventuelle
   * @param item l'objet sélectionné
   */
  public itemClick($event, item: any) {
    // console.log('itemClick',item);
    // en mode multi, neutralisation de la fermeture
    if (this.isMultiSelection()) {
      // console.log('###itemClick','multiMode intercept');
      $event.stopImmediatePropagation();
      $event.preventDefault();
      // focus OK. Mais écouteur clavier hors de portée
      this.selectOption(this.getByItem(this.dropPanel.options, item)); // et prise de focus par l'élément
      return;
    }

    // sinon, OK
    if (item === EXO_VALUE.FAKE) {
      this.doSelectOne(null);
      // console.log('doSelectOne');
    } else {
      this.doSelectOne(item);
    }
  }

  /** clic sur une checkbox de la dropDown (mode multi)
   * @param item l'objet sélectionné
   */
  public checkClick(item: any) {
    // console.log('###checkClick','multiMode intercept');//,$event,item);

    if (this.popUpSelectedItems.has(item)) {
      // déjà sélectionné, je suis en train d'enleve
      this.popUpSelectedItems.delete(item);
    } else {
      // sinon j'ajoute
      this.popUpSelectedItems.set(item, item);
    }

    this.manageSelectAll();
  }

  /** définition du template à utiliser pour les rendus :  */
  public getTemplate() {
    if (this.wizItemRenderer) {
      return this.dynamicRenderer;
    }
    return this.defaultRenderer;
  }

  /**
   * LMA fix : sinon [object Object] sur sélection self
   * Autcomplete function (use to display selection)
   * @param item, option du mat autocomplete
   */
  public displayFn(item: any): string {
    return this.getInnerLabel();
  }

  // ####
  // ####
  // #### navigation clavier ####
  // ####
  // ####

  /** alimenté UNIQUEMENT sur open, click + select par space (multi) */
  public preventClosure: boolean = false;
  /** sélection d'un item par click (mode multi) puis space: éviter la fermeture de la pop */
  public optSelect(e: MatAutocompleteSelectedEvent) {
    if (this.isMultiSelection()) {
      // console.log('preventClosure',e);
      this.preventClosure = true;
    }
  }

  /** interception du keydown dans la popUp */
  public _handleKeydownFromPanel(event: KeyboardEvent): void {
    this._handleKeydown(event);
  }

  /** fait partie des combinaisons globales de raccourcis */
  private isGlobalShortcut(event: KeyboardEvent): boolean {
    const keyCode = event.key.toLowerCase();
    let result: boolean = false;
    if (keyCode === VALUE_X && event.shiftKey && event.ctrlKey) {
      result = true;
    } else if (keyCode === VALUE_L && event.shiftKey && event.ctrlKey) {
      result = true;
    }
    return result;
  }

  /** interception du keydown dans l'input comme dans la popUp */
  public _handleKeydown(event: KeyboardEvent): void {
    const keyCode = event.key;
    // console.log('_handleKeydown', keyCode);

    // fleche haut / bas : ouverture de la pop, navigation si ouverte
    const isArrowKey = keyCode === VALUE_UP || keyCode === VALUE_DOWN;
    // enter : fermeture de la pop, avec validation
    const isEnterKey = keyCode === VALUE_ENTER;
    // Escape : fermeture de la pop, avec rollback
    const isEscapeKey = keyCode === VALUE_ESCAPE;
    // space ouverture de la pop, sélection et fermeture si mono, toggle check si multi
    const isSpaceKey = event.code === CODE_SPACE; // Key.VALUE_SPACE;
    // tab avec drop ouvert, close
    const isTabKey = event.code === VALUE_TAB;
    // suppr : backspace ouverture de la pop si mode search
    const isDelKey = keyCode === VALUE_DELETE || keyCode === VALUE_BACK_SPACE;

    if (isArrowKey) {
      if (!this.popUpOpened) {
        this.askForOpenPanel();
        this.selectOption();
      } else {
        this.scrollToOption(keyCode);
      }
    }

    if (isDelKey && this.wizShowSearchInput) {
      if (!this.popUpOpened) {
        this.askForOpenPanel();
        this.selectOption();
      }
    }

    if (isEscapeKey) {
      if (this.popUpOpened) {
        // rollback sur les éléments sélectionnés
        this.updatePopSelectedItems();
        this.askFromClosePanel();
      }
    }

    // LMA : ctrl + shift + L / X utilisé en raccourcis globaux : cancel si drop
    if (this.isGlobalShortcut(event)) {
      if (this.popUpOpened) {
        // rollback sur les   éléments sélectionnés
        this.updatePopSelectedItems();
        this.askFromClosePanel();
      }
    }

    if (isEnterKey || isTabKey) {
      // validation sur Enter / Tab. Tab va aussi naviger (pas de prevent)
      if (this.popUpOpened) {
        if (this.isMultiSelection()) {
          // rien, déjà check
        } else {
          if (this.selectedOption) {
            // console.log('isEnterKey',this.selectedOption.value);
            this.doSelectOne(this.selectedOption.value); // sélection de l'actif
          }
        }
        this.innerAsTrigger.closePanel();
      }
    }

    if (isSpaceKey) {
      // console.log('isSpaceKey', event, this.wizShowSearchInput, this.popUpOpened);
      if (this.wizShowSearchInput) {
        // en mode search, neutralisation de space (sinon pas saisissable)
        // console.log('isSpaceKey','on search mode');
      } else {
        // mode non éditable
        if (!this.popUpOpened) {
          // pas de pop, ouverture
          this.askForOpenPanel();
          this.selectOption();
        } else {
          // sinon coche ou select & close
          if (this.isMultiSelection()) {
            if (this.selectedOption.value === 'SELECT_ALL') {
              if (this.selectAllCheck.wizChecked === true) {
                // inversion de la coche
                this.selectAllCheck.wizChecked = false;
              } else {
                this.selectAllCheck.wizChecked = true;
              }
              this.toggleSelectAll(null); // et appel de la méthode de toggle
            } else {
              this.checkClick(this.selectedOption.value); // cocher / décocher
            }
          } else {
            this.doSelectOne(this.selectedOption.value); // sélection de l'actif
            this.innerAsTrigger.closePanel();
          }
        }
      }
    } // fin de gestion de space

    // neutralisation des touches custom
    if (isArrowKey || isEnterKey || (isSpaceKey && !this.wizShowSearchInput) || isEscapeKey) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /** en mode saisie, sur keydown, gestion du filtre de la popUp */
  public _handleKeyup(event: KeyboardEvent): void {
    if (!this.wizShowSearchInput) {
      // uniquement en mode saisie
      return;
    }

    // console.log('_handleKeyup neutralize',event.key);
    const keyCode = event.key;
    // fleche haut / bas : ouverture de la pop, navigation si ouverte
    const isArrowKey = keyCode === VALUE_UP || keyCode === VALUE_DOWN;
    // enter : fermeture de la pop, avec validation
    const isEnterKey = keyCode === VALUE_ENTER;
    // Escape : fermeture de la pop, avec rollback
    const isEscapeKey = keyCode === VALUE_ESCAPE;
    // space ouverture de la pop, sélection et fermeture si mono, toggle check si multi
    const isSpaceKey = event.code === CODE_SPACE; // 'Space';

    // neutralisation des touches custom
    if (isArrowKey) {
      // console.log('_handleKeyup neutralize',event.key);
    } else if (isEnterKey) {
      // laisser finir de filter la collection
      setTimeout(() => {
        // console.log('show Panel',this.dropPanel.panel);
        this.selectOption(0); // puis sélectionner le premier
      }, 0);
      // console.log('_handleKeyup intercept',event.key);
    } else if (isEscapeKey) {
      // console.log(this.getWizValue(), this.getLastGoodValue());
      // attention, peut-être pas de changement de valeur, forcer le refresh de la zone de saisie
      this.setWizValue(this.getLastGoodValue());
      this.innerInput.nativeElement.value = this.getInnerLabel();
      // synchronisation des popupSelectedItems pour correspondre à la wizvalue
      this.updatePopSelectedItems();
    } else {
      // texte véritable
      // raffrachissement de la donnée grâce au filtre et selection de la première valeur de la liste
      this.refreshDataAndSelectOptions();
    }
    // LMA TODO suppr + backspace : annuler selecteddItem

    // neutralisation des touches custom
    if (isArrowKey || isEnterKey || (isSpaceKey && !this.wizShowSearchInput) || isEscapeKey) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /** l'option qui porte le marqueur de focus */
  private selectedOption: MatOption = null;
  /** l'index de l'option en cours */
  private currentIdx: number = 0;

  /** accesseur sur le tableau d'option par l'item (l'objet) */
  private getByItem(options: QueryList<MatOption>, item: any): number {
    return options.toArray().findIndex((x) => x.value === item);
  }

  /** accesseur sur le tableau d'option par l'index de la MatOption */
  private getByIndex(options: QueryList<MatOption>, x: number): MatOption {
    return options.find((_, i) => i === x);
  }

  /**
   * Indique si le mode multiselection est actif
   */
  public isMultiSelection(): boolean {
    return !!this.wizDataProvider && this.wizDataProvider.selectionMode === 'multiple';
  }

  /**
   * sur ouverture / navigation clavier, sélection de l'option "focus"
   * @param idx, index de l'élement à enregistrer comme selectedOption
   */
  public selectOption(idx: number = null): void {
    if (this.selectedOption) {
      // suppression de la marque de sélection active
      this.selectedOption.setInactiveStyles();
    }

    // lorsque l'index n'est pas donné, on recherche l'index de l'élément actuellement selectionné
    if (idx === null && this.wizDataProvider) {
      const index = this.getByItem(this.dropPanel.options, this.wizDataProvider.selectedItem);
      idx = index > -1 ? index : 0; // si aucun élement n'est sélectionné on donne l'index 0
    }

    this.currentIdx = idx;
    this.selectedOption = this.getByIndex(this.dropPanel.options, idx);
    if (this.selectedOption) {
      this.selectedOption.setActiveStyles();
    }
    // console.log('selectOption',this.selectedOption);
  }

  /**
   * navigation clavier dans la popUp + gestion du scroll
   */
  private scrollToOption(keyCode: string): void {
    // on recherche l'option active
    // const isArrowKey = keyCode === Key.VALUE_UP || keyCode === Key.VALUE_DOWN;
    if (keyCode === VALUE_UP && this.currentIdx > 0) {
      this.currentIdx--;
      this.selectOption(this.currentIdx);
    } else if (keyCode === VALUE_DOWN && this.currentIdx < this.wizDataProvider.length) {
      this.currentIdx++;
      this.selectOption(this.currentIdx);
    }

    // synchro du scroll de la pop
    this.dropPanel._setScrollTop(this.currentIdx * this.itemHeight);
  }

  /** construction du libellé de l'input depuis la wizValue */
  private getInnerLabel(): string {
    if (this.wizDataProvider && this.wizDataProvider.selectedItems && this.wizDataProvider.selectedItems.length) {
      return this.wizDataProvider.selectedItems.map((item) => this.getItemLabel(item)).join(', ');
    }
    return '';
  }

  /** accesseur sur le libellé d'un item sélectionné */
  public getItemLabel(item: any): string {
    if (!item) {
      return '';
    }
    return this.wizLabelField ? item[this.wizLabelField] : item;
  }

  /**
   * rafraichi la donnée en appliquant les filtres et rend actif le premier élément de la drop
   */
  public refreshDataAndSelectOptions(): void {
    this.wizDataProvider.refresh();
    // laisser finir de filtrer la collection
    setTimeout(() => {
      this.selectOption(0); // puis sélectionner le premier
    }, 0);
  }

  /** clic sur la croix de nettoyage en mode search */
  public clearFilter(e): void {
    e.stopPropagation();
    if (this.wizDisabled) {
      return;
    }
    this.innerInput.nativeElement.value = ''; // forcer le retour à une zone vide, on est peut-être en texte non conforme
    this.innerInput.nativeElement.focus(); // pour feed initialValue et gestion cycle focusOus

    this.wizDataProvider.refresh();
    // this.wizDataProvider.select([]);
    // this.doSelectOne(null); // mise à jour des elements selectionnés de la drop
    // this.refreshDataAndSelectOptions();
  }

  /** application du style effectif (disabled, pointer) */
  public checkStyle() {
    if (this.wizDisabled) {
      // inactif
      this.renderer.addClass(this.elementRef.nativeElement, this.disabledClassName);
      this.renderer.removeClass(this.elementRef.nativeElement, this.buttonModeClassName);
    } else {
      // actif
      this.renderer.removeClass(this.elementRef.nativeElement, this.disabledClassName);
      if (!this.wizShowSearchInput)
        // pointer si pas mode saisie
        this.renderer.addClass(this.elementRef.nativeElement, this.buttonModeClassName);
    }
  }

  /** suppression / affichage des marqueurs d'erreur & messages */
  protected manageErrorDisplay(): void {
    // console.log('manageErrorDisplay', this._hasErrors);
    if (this._hasErrors) {
    } else {
      if (this.getErrorVCRef()) {
        // clear container
        this.getErrorVCRef().clear();
      }
    }
    this.cdref.markForCheck();
  }

  /**
   * Appel externe sur focusOut depuis la directive wizValidator
   * @param pErrors la collection d'erreur
   * @param wizShowErrors : //TODO JRO demande externe ? sert à rien ici
   */
  public onWizValidation(pErrors: WizValidationErrors, wizShowErrors: boolean): void {
    this._hasErrors = pErrors.length > 0;
    // console.log('onWizValidation',this._hasErrors);
    this.manageErrorDisplay();
  }

  // ####
  // ####
  // #### contraintes ####
  // ####
  // ####

  /** Component's required error translation key */
  private _wizRequiredErrorMessage: string;
  @Input() public set wizRequiredErrorMessage(str: string) {
    this.setWizRequiredErrorMessage(str);
  }
  public get wizRequiredErrorMessage(): string {
    return this.getWizRequiredErrorMessage();
  }
  /**
   * methode surchargeable du message d'erreur saisi de champ obligatoire
   */
  protected setWizRequiredErrorMessage(str: string) {
    this._wizRequiredErrorMessage = str;
    this.cdref.markForCheck();
  }
  /** accesseur sur le message d'erreur saisi de champ obligatoire */
  protected getWizRequiredErrorMessage(): string {
    return this._wizRequiredErrorMessage;
  }

  /** contrat d'interface pour la directive wizValidator */
  public getValidators(): WizValidatorFn[] {
    const validators: WizValidatorFn[] = [];
    // if wizMandatory is true, the required validator in added to wizValidatorsArray
    if (this.wizMandatory) {
      const errorMessage = this.wizRequiredErrorMessage ? this.wizRequiredErrorMessage : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(WizValidators.wizDropdownRequired(errorMessage));
    }
    // gestion search non trouvé : comparaison innerInputString & innerInput.native.value
    if (this.wizShowSearchInput) {
      // TODO : chaine spécifique
      const errorMessage = this.wizRequiredErrorMessage ? this.wizRequiredErrorMessage : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(this.getRequiredValidator(errorMessage));
    }

    return validators;
  }

  /**
   * controle de cohérence de la chaine en cours de saisie
   * @param errorMessage, the errormessage
   */
  protected getRequiredValidator(errorMessage: string): WizValidatorFn {
    return (compo: WizDropdown): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage,
      };

      let haserror: boolean = false;
      if (this.innerInput.nativeElement.value && this.wizDataProvider.length === 0) {
        // console.log('customValid','error');
        haserror = true;
      }
      return haserror ? [temp] : null;
    };
  }

  // container d'affichage des erreurs
  public getErrorVCRef(): ViewContainerRef {
    return this.errorContainer;
  }

  /**
   * Value rollback
   */
  public wizRollback(): void {
    // console.log('wizRollback');
    this.innerInput.nativeElement.value = this.getInnerLabel(); // revert de la mauvaise saisie éventuelle
    this.updatePopSelectedItems(); // mise à jour des selectedItems de la drop, pour qu'à la fermeture de la drop, on set la bonne valeur
  }

  /**
   * override,
   * à la suite d'un clic outside le focusManager appelle wizLoseFocus
   * on override cette methode pour gérer le cas où le clic outside est fait pour fermer la drop
   * TODO retirer lorsque le focusmanager gérera la fermeture des composants à overlay lors d'un clic outside
   */
  public wizLoseFocus(): void {
    // mode manuel, la pop est ouverte et visible (this.dropPanel.isOpen = true, cela veut dire que le panel est visible)
    if (this.wizShowSearchInput && this.popUpOpened && this.dropPanel.isOpen) {
      this.popUpOpened = false;
      // suppression du message, à ce stade le compo à toujours le focus, le message d'erreur sera affiché à la perte de focus réelle
      this._hasErrors = false;
      this.manageErrorDisplay();
    } else {
      super.wizLoseFocus();
    }
  }
}
